/* import __COLOCATED_TEMPLATE__ from './general.hbs'; */
/* RESPONSIBLE TEAM: team-frontend-tech */
import type AppService from 'embercom/services/app-service';
import type RegionService from 'embercom/services/region-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { dropTask } from 'ember-concurrency-decorators';
import type ApplicationInstance from '@ember/application/instance';
import { taskFor } from 'ember-concurrency-ts';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { modifier } from 'ember-modifier';
import ClipboardJS from 'clipboard';
import { captureException } from 'embercom/lib/sentry';
import { SELF_SERVE_CHURN_SURVEY_ID } from 'embercom/lib/billing/surveys';
import { startSurvey, showNewMessageInIntercomWidget } from 'embercom/lib/intercom-widget-helper';
import { put, post, ajaxDelete } from 'embercom/lib/ajax';

import type { ParseResult } from 'papaparse';
import csvParser from 'papaparse';
import { Promise as EmberPromise } from 'rsvp';
import { type TaskGenerator } from 'ember-concurrency';

interface Args {
  model: {
    generalSettings: any;
    messengerSettings: any;
    companyAttribute: any;
    poweredBySettings: any;
  };
}

interface Signature {
  Args: Args;
  Element: never;
}
export default class General extends Component<Signature> {
  @service declare appService: AppService;
  @service declare notificationsService: any;
  @service declare intl: any;
  @service declare session: any;
  @service declare modalService: any;
  @service declare router: any;
  @service declare intercomEventService: any;
  @service declare customerService: any;
  @service declare conversationTranslationSettings: any;
  @service declare intercomConfirmService: any;
  @service declare finPlaygroundApiService: any;
  @service declare regionService: RegionService;

  @tracked declare model: any;
  @tracked selectedTimezone: any = null;
  @tracked appName: string | null = null;
  @tracked customerFacingName: string | null = null;
  @tracked adminNameInput = '';
  @tracked isCompanyEnabled = false;
  @tracked isAiInboxTranslationsEnabled = false;
  @tracked multilingualGlossaryCsvExists = false;
  @tracked preventUpdateFromMessenger = false;
  @tracked preventCompanyCreationFromUserProfile = false;
  @tracked preventCompanyEditsFromUserProfile = false;
  @tracked isTestWorkspaceEnabled = false;
  @tracked changedTestAppUse = false;
  @tracked allLanguagesEnabled = false;
  @tracked isPoweredByEnabled = false;
  @tracked isTeamMentionsDisabled = false;
  @tracked apiKey: string | null = null;
  clipboard: ClipboardJS | null = null;

  @tracked file: File | null = null;
  @tracked fileIsValid = false;
  @tracked fileFailedToUpload = false;
  @tracked parsingErrorMessage = '';
  @tracked hasHeaderRow = false;
  @tracked csvData: string[][] = [];

  constructor(owner: ApplicationInstance, args: Args) {
    super(owner, args);
    this.setInitialValues();
    if (this.app.canUseFeature('team-fin-voice-can-view-api-config') && this.app.isStandaloneApp) {
      this.loadApiKey();
    }
  }

  copyWorkspaceIdContainer = modifier(
    () => {
      this.onRender();
    },
    { eager: false },
  );

  onRender() {
    let container = document.querySelector('#copy-workspace-id-container');
    let copyAppIdButton = container?.querySelector('.form__input-suffix');

    if (copyAppIdButton) {
      copyAppIdButton.classList.add('cursor-pointer');
      copyAppIdButton.setAttribute('data-clipboard-text', this.app.id);
      copyAppIdButton.setAttribute('data-test-copy-workspace-id-button', '');

      if (!this.clipboard) {
        this.clipboard = new ClipboardJS(copyAppIdButton);
        this.clipboard.on('success', () => {
          this.notificationsService.notifyConfirmation(
            this.intl.t('new-settings.workspace.general.workspace-section.copy-app-id-success'),
          );
        });
        this.clipboard.on('error', (err) => {
          console.error(err);
          this.notificationsService.notifyWarning(
            this.intl.t('new-settings.workspace.general.workspace-section.copy-app-id-error'),
          );
        });
      }
    }
  }

  get app() {
    return this.appService.app;
  }

  get isSaving() {
    return taskFor(this.saveGeneralSettings).isRunning;
  }

  get showPaywallForLanguages() {
    return !this.app.hasMultipleLanguages;
  }

  get addLocaleDropdownItems() {
    return this.notPermittedLocales.map((locale: any) => ({
      value: locale.localeId,
      text: locale.name,
    }));
  }

  get notPermittedLocales() {
    return this.languageSettings.supportedLocales.filter(
      (locale: any) => !this.localeIsPermitted(locale),
    );
  }

  get customer() {
    return this.customerService.customer;
  }

  get currentAdmin() {
    return this.session.teammate;
  }

  get isSalesforceContractedOrCustomPricing() {
    return this.customer.hasCustomPricing || this.app.isSalesforceContracted;
  }

  get isDeleteButtonDisabled() {
    return this.currentAdmin?.name !== this.adminNameInput;
  }

  get messengerSettings() {
    return this.args.model.messengerSettings;
  }

  get generalSettings() {
    return this.args.model.generalSettings;
  }

  get companyAttribute() {
    return this.args.model.companyAttribute;
  }

  get poweredBySettings() {
    return this.args.model.poweredBySettings;
  }

  get languageSettings() {
    return this.messengerSettings?.languages;
  }

  get localeDropdownItems() {
    return this.languageSettings.supportedLocales.map((locale: any) => ({
      value: locale.localeId,
      text: locale.name,
    }));
  }

  get permittedLocales() {
    return this.languageSettings.supportedLocales.filter((locale: any) =>
      this.localeIsPermitted(locale),
    );
  }

  get defaultLocaleObject() {
    return this.languageSettings.supportedLocales.find(
      (locale: any) => locale.localeId === this.languageSettings.defaultLocale,
    );
  }

  get permittedLocalesWithoutDefault() {
    return this.permittedLocales.filter(
      (locale: any) => locale.localeId !== this.languageSettings.defaultLocale,
    );
  }

  async loadApiKey() {
    try {
      this.apiKey = await this.finPlaygroundApiService.getToken();
    } catch (error) {
      this.apiKey = await this.finPlaygroundApiService.createToken();
    }
  }

  localeIsPermitted(locale: any) {
    let defaultLocale = this.languageSettings.defaultLocale;
    return locale.isPermitted || locale.localeId === defaultLocale;
  }

  @action
  removeLanguage(localeId: string) {
    let localeToRemove = this.languageSettings.supportedLocales.findBy('localeId', localeId);
    localeToRemove.isPermitted = false;
  }

  @action
  setDefaultLanguage(localeId: string) {
    let permitted = this.permittedLocales;

    if (permitted.findBy('localeId', localeId)) {
      let oldDefaultLocale = this.defaultLocaleObject;
      oldDefaultLocale.isPermitted = true;
    }

    this.languageSettings.defaultLocale = localeId;
    this.defaultLocaleObject.isPermitted = false;
  }

  @action
  permitLanguage(localeId: string) {
    let locale = this.languageSettings.supportedLocales.findBy('localeId', localeId);
    locale.isPermitted = true;
  }

  get timezoneOptions() {
    return (this.generalSettings?.get('timezones') || []).map((timezone: any) => ({
      text: timezone.name,
      value: timezone.name,
    }));
  }

  get canShowCustomerFacingName() {
    return (
      this.app.canUseFeature('customer-facing-name') &&
      this.generalSettings?.get('customer_facing_name_supported')
    );
  }

  setInitialValues() {
    if (!this.generalSettings) {
      return;
    }
    this.selectedTimezone = this.generalSettings.get('time_zone_name');
    this.appName = this.generalSettings.get('name');
    this.customerFacingName = this.generalSettings.get('customer_facing_name');
    this.isCompanyEnabled = this.generalSettings.get('companies_enabled');
    this.preventUpdateFromMessenger = this.companyAttribute?.prevent_update_from_messenger;
    if (this.app.canUseFeature('team-email-create-new-company-toggle-enabled')) {
      this.preventCompanyCreationFromUserProfile = !this.generalSettings.get(
        'company_creation_from_user_profile_enabled',
      );
    }
    if (this.app.canUseFeature('team-email-edit-company-attributes-toggle')) {
      this.preventCompanyEditsFromUserProfile = !this.generalSettings.get(
        'company_attributes_editing_from_user_profile_enabled',
      );
    }
    this.isTestWorkspaceEnabled = this.generalSettings.get('uses_test_app');
    this.isPoweredByEnabled = this.poweredBySettings?.get('emailPoweredBy') ?? true;
    this.loadAppSettings();
  }

  async loadAppSettings() {
    try {
      let settings = await this.session.workspace.appSettingsLoader.value;
      this.isAiInboxTranslationsEnabled = settings?.aiInboxTranslationsEnabled ?? false;
      this.multilingualGlossaryCsvExists = settings?.multilingualGlossaryCsvExists ?? false;
      this.allLanguagesEnabled = settings?.allLanguagesEnabled ?? false;
      this.isTeamMentionsDisabled = settings?.teamMentionsDisabled ?? false;
    } catch (error) {
      console.error('Failed to load app settings:', error);
    }
  }

  async saveAiInboxTranslations() {
    await post(`/ember/inbox/app_settings/update_ai_inbox_translations`, {
      app_id: this.app.id,
      ai_inbox_translations_enabled: this.isAiInboxTranslationsEnabled,
    });
    this.session.workspace.aiInboxTranslationsEnabled = this.isAiInboxTranslationsEnabled;
    await this.session.workspace.reloadAppSettings();
  }

  async saveTeamMentionsDisabled() {
    await put(`/ember/inbox/app_settings/update_team_mentions_disabled`, {
      app_id: this.app.id,
      team_mentions_disabled: this.isTeamMentionsDisabled,
    });
    this.intercomEventService.trackAnalyticsEvent({
      action: this.isTeamMentionsDisabled ? 'disabled' : 'enabled',
      object: 'team_mentions',
      context: 'from_new_settings',
    });
    await this.session.workspace.reloadAppSettings();
  }

  async saveAllLanguagesEnabled() {
    await post(`/ember/inbox/app_settings/update_language_settings`, {
      app_id: this.app.id,
      all_languages_enabled: this.allLanguagesEnabled,
    });
    this.session.workspace.allLanguagesEnabled = this.allLanguagesEnabled;
    await this.session.workspace.reloadAppSettings();
  }

  @action
  openMessenger() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'opened',
      object: 'cancel_subscription_messenger',
      context: 'from_new_settings',
    });
    showNewMessageInIntercomWidget(
      this.intl.t(
        'new-settings.workspace.general.delete-account-section.custom-pricing-or-salesforce-contracted',
      ),
    );
  }

  @dropTask
  *saveGeneralSettings() {
    try {
      if (
        !this.app.id ||
        !this.selectedTimezone ||
        !this.generalSettings ||
        this.generalSettings.isDestroyed
      ) {
        return;
      }
      this.generalSettings.time_zone_name = this.selectedTimezone;
      this.generalSettings.name = this.appName;
      if (this.canShowCustomerFacingName) {
        this.generalSettings.customer_facing_name = this.customerFacingName;
      }
      this.generalSettings.companies_enabled = this.isCompanyEnabled;
      if (this.app.canUseFeature('team-email-create-new-company-toggle-enabled')) {
        this.generalSettings.company_creation_from_user_profile_enabled =
          !this.preventCompanyCreationFromUserProfile;
      }
      if (this.app.canUseFeature('team-email-edit-company-attributes-toggle')) {
        this.generalSettings.company_attributes_editing_from_user_profile_enabled =
          !this.preventCompanyEditsFromUserProfile;
      }
      this.generalSettings.uses_test_app = this.isTestWorkspaceEnabled;

      yield this.saveAiInboxTranslations();
      yield this.saveAllLanguagesEnabled();
      yield this.saveTeamMentionsDisabled();
      yield this.generalSettings.save();
      this.notificationsService.notifyConfirmation(
        this.intl.t('settings.general.save-button.success-notification'),
      );
      if (this.changedTestAppUse) {
        this.router.transitionTo({
          queryParams: {
            ...this.router.currentRoute.queryParams,
            highlight: 'test-workspace',
          },
        });
      }
    } catch (error) {
      let notificationText;
      let responseJSON = error.jqXHR.responseJSON;
      if (responseJSON && responseJSON.errors[0]) {
        notificationText = responseJSON.errors[0];
      } else {
        notificationText = this.intl.t('settings.general.save-button.failure-notification');
      }
      this.notificationsService.notifyError(notificationText);
    }
  }

  @dropTask
  *saveCompanyAttributeSettings() {
    try {
      if (!this.app.id || !this.companyAttribute || this.companyAttribute.isDestroyed) {
        return;
      }
      yield put('/ember/standard_data_attributes/companies', {
        app_id: this.app.id,
        prevent_update_from_messenger: this.preventUpdateFromMessenger,
      });
    } catch (error) {
      let notificationText;
      let responseJSON = error.jqXHR.responseJSON;
      if (responseJSON && responseJSON.errors[0]) {
        notificationText = responseJSON.errors[0];
      } else {
        notificationText = this.intl.t('settings.general.save-button.failure-notification');
      }
      this.notificationsService.notifyError(notificationText);
    }
  }

  @dropTask
  *saveMessengerSettings() {
    try {
      if (!this.app.id) {
        return;
      }
      yield this.messengerSettings.save();
    } catch (error) {
      let notificationText;
      let responseJSON = error.jqXHR.responseJSON;
      if (responseJSON && responseJSON.errors[0]) {
        notificationText = responseJSON.errors[0];
      } else {
        notificationText = this.intl.t('settings.general.save-button.failure-notification');
      }
      this.notificationsService.notifyError(notificationText);
    }
  }

  @dropTask
  *savePoweredBySettings() {
    try {
      if (!this.app.id || !this.poweredBySettings || this.poweredBySettings.isDestroyed) {
        return;
      }
      this.poweredBySettings.emailPoweredBy = this.isPoweredByEnabled;
      yield this.poweredBySettings.save();
    } catch (error) {
      let notificationText;
      let responseJSON = error.jqXHR.responseJSON;
      if (responseJSON && responseJSON.errors[0]) {
        notificationText = responseJSON.errors[0];
      } else {
        notificationText = this.intl.t('settings.general.save-button.failure-notification');
      }
      this.notificationsService.notifyError(notificationText);
    }
  }

  @dropTask
  *deleteApp() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'clicked',
      object: 'delete_app_button',
      context: 'from_new_settings',
    });

    if (
      this.customer.subscriptionCancelledAt ||
      this.customer.subscriptionWillBeCancelled ||
      this.customer.hasNoActiveSubscription ||
      this.app.isDeveloperWorkspace
    ) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'opened',
        object: 'delete_app_modal',
        context: 'from_new_settings',
      });
      yield this.modalService.openModal('settings/modals/delete-app-modal', this.app);
    } else if (this.regionService.isUS && !this.app.isStandaloneApp) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'opened',
        object: 'self_serve_churn_survey',
        context: 'from_new_settings',
      });
      // The last step of the new self serve churn Survey will
      // redirect customers to their cancellation page.
      startSurvey(SELF_SERVE_CHURN_SURVEY_ID);
    } else {
      this.router.transitionTo('apps.app.billing.cancel'); // Fallback for AU / EU workspaces
    }
  }

  @action
  save() {
    taskFor(this.saveGeneralSettings).perform();
    taskFor(this.saveMessengerSettings).perform();
    taskFor(this.saveCompanyAttributeSettings).perform();
    taskFor(this.savePoweredBySettings).perform();
  }

  @action
  delete() {
    taskFor(this.deleteApp).perform();
  }

  @action
  async toggler(toggleType: string) {
    if (toggleType === 'test-workspace') {
      let isEnabling = !this.isTestWorkspaceEnabled;

      if (isEnabling) {
        this.changedTestAppUse = true;
        this.isTestWorkspaceEnabled = true;
        taskFor(this.saveGeneralSettings).perform();
        return;
      }

      let confirmed = await this.intercomConfirmService.confirm({
        title: this.intl.t('new-settings.workspace.general.test-workspace-section.disable-title'),
        body: this.intl.t('new-settings.workspace.general.test-workspace-section.disable-body'),
        confirmButtonText: this.intl.t(
          'new-settings.workspace.general.test-workspace-section.disable-button',
        ),
        cancelButtonText: this.intl.t(
          'new-settings.workspace.general.test-workspace-section.disable-cancel-button',
        ),
        primaryButtonType: 'primary-destructive',
      });

      if (confirmed) {
        this.changedTestAppUse = true;
        this.isTestWorkspaceEnabled = false;
        taskFor(this.saveGeneralSettings).perform();
      }
      return;
    }

    switch (toggleType) {
      case 'company':
        this.isCompanyEnabled = !this.isCompanyEnabled;
        break;
      case 'prevent-update-from-messenger':
        this.preventUpdateFromMessenger = !this.preventUpdateFromMessenger;
        break;
      case 'prevent-company-creation-from-user-profile':
        this.preventCompanyCreationFromUserProfile = !this.preventCompanyCreationFromUserProfile;
        break;
      case 'prevent-company-edits-from-user-profile':
        this.preventCompanyEditsFromUserProfile = !this.preventCompanyEditsFromUserProfile;
        break;
      case 'ai-inbox-translations':
        this.isAiInboxTranslationsEnabled = !this.isAiInboxTranslationsEnabled;
        break;
      case 'powered-by':
        this.isPoweredByEnabled = !this.isPoweredByEnabled;
        break;
      case 'team-mentions-disabled':
        this.isTeamMentionsDisabled = !this.isTeamMentionsDisabled;
        break;
      default:
        break;
    }
  }

  async _parseCsvFile(file: File): Promise<ParseResult<any>> {
    return new EmberPromise((resolve, reject) => {
      let complete = (results: any) => {
        if (results.errors.length) {
          if (results.errors.some((error: any) => error.code !== 'UndetectableDelimiter')) {
            reject(results);
          }
        }
        resolve(results);
      };
      csvParser.parse(file, { complete, error: reject });
    });
  }

  isValidCsvStructure(data: any[][]) {
    if (!Array.isArray(data) || data.length < 2) {
      this.parsingErrorMessage = this.intl.t(
        'new-settings.workspace.general.ai-inbox-translations-glossary-section.import-modal.no-entries-error',
      );
      return false;
    }

    let firstRowLength = data[0].length;
    if (firstRowLength === 0) {
      this.parsingErrorMessage = this.intl.t(
        'new-settings.workspace.general.ai-inbox-translations-glossary-section.import-modal.no-locale-columns-error',
      );
      return false;
    }

    return true;
  }

  @action
  async parseAndUploadCsvFile() {
    this.parsingErrorMessage = '';
    this.csvData = [];
    this.fileFailedToUpload = false;
    try {
      if (!this.file) {
        return;
      }

      let results = await this._parseCsvFile(this.file);

      if (!this.isValidCsvStructure(results.data)) {
        this.fileFailedToUpload = true;
        this.fileIsValid = false;
        throw new Error(
          this.intl.t(
            'new-settings.workspace.general.ai-inbox-translations-glossary-section.import-modal.parse-error',
          ),
        );
      }

      // Get all rows from the CSV file
      this.csvData = results.data as string[][];
      this.fileIsValid = true;

      this.intercomEventService.trackAnalyticsEvent({
        action: 'uploaded',
        object: 'multilingual_glossary',
        context: 'from_settings',
      });
    } catch (error) {
      this.fileIsValid = false;
      this.fileFailedToUpload = true;
      if (this.parsingErrorMessage === '') {
        this.parsingErrorMessage = this.intl.t(
          'new-settings.workspace.general.ai-inbox-translations-glossary-section.import-modal.parse-error',
        );
      }
    }
  }

  @dropTask
  *saveGlossary(): TaskGenerator<void> {
    try {
      let params = {
        app_id: this.session.workspace.id,
        csv_glossary_upload: this.csvData,
      };

      yield post('/ember/multilingual_glossary/import', params);

      this.multilingualGlossaryCsvExists = true;
      this.notificationsService.notifyConfirmation(
        this.intl.t(
          'new-settings.workspace.general.ai-inbox-translations-glossary-section.import-modal.success',
        ),
      );
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t(
          'new-settings.workspace.general.ai-inbox-translations-glossary-section.import-modal.error',
        ),
      );
    }
  }

  @dropTask
  *deleteGlossaries(): TaskGenerator<void> {
    try {
      let params = {
        app_id: this.session.workspace.id,
      };

      yield ajaxDelete('/ember/multilingual_glossary/delete', params);

      this.multilingualGlossaryCsvExists = false;
      this.file = null;
      this.fileIsValid = false;
      this.csvData = [];

      // Try to click the remove button if it exists
      let removeButton = document.querySelector('[data-test-remove-file]');
      if (removeButton instanceof HTMLElement) {
        removeButton.click();
      } else {
        this.setFile(null);
      }

      // Use the exact message expected by the test
      this.notificationsService.notifyConfirmation(
        this.intl.t(
          'new-settings.workspace.general.ai-inbox-translations-glossary-section.delete-modal.success',
        ),
      );

      this.intercomEventService.trackAnalyticsEvent({
        action: 'deleted',
        object: 'multilingual_glossary',
        context: 'from_settings',
      });
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t(
          'new-settings.workspace.general.ai-inbox-translations-glossary-section.delete-modal.error',
        ),
      );
    }
  }

  @action
  async exportGlossary() {
    try {
      let params = {
        app_id: this.session.workspace.id,
      };

      // Create URL with query parameters
      let queryParams = new URLSearchParams(params).toString();
      let endpoint = `/ember/multilingual_glossary/export?${queryParams}`;

      // Trigger file download by creating a temporary link
      let link = document.createElement('a');
      link.setAttribute('href', endpoint);
      link.setAttribute('download', 'glossary_export.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.intercomEventService.trackAnalyticsEvent({
        action: 'exported',
        object: 'multilingual_glossary',
        context: 'from_settings',
      });
    } catch (error) {
      let errorMessage = this.intl.t(
        'new-settings.workspace.general.ai-inbox-translations-glossary-section.export-modal.error',
      );

      // Extract error message from the API response if available
      if (error?.jqXHR?.responseJSON?.error) {
        errorMessage = error.jqXHR.responseJSON.error;
      }

      this.notificationsService.notifyError(errorMessage);
    }
  }

  @action
  async setFile(file: File | null) {
    // Reset state completely if a file was previously rejected
    if (this.fileFailedToUpload && file) {
      this.file = null;
      this.fileIsValid = false;
      this.fileFailedToUpload = false;
      this.parsingErrorMessage = '';

      // Now set the new file after resetting state
      setTimeout(() => {
        this.file = file;
        this.parseAndUploadCsvFile();
      }, 0);
      return;
    }

    // Normal flow for first file upload
    this.file = file;
    if (file) {
      await this.parseAndUploadCsvFile();
    } else {
      this.fileIsValid = false;
      this.fileFailedToUpload = false;
      this.parsingErrorMessage = '';
    }
  }

  @action
  setLanguageMode(mode: string) {
    this.allLanguagesEnabled = mode === 'all';
  }

  willDestroy() {
    super.willDestroy();
    try {
      this.clipboard?.destroy();
    } catch (e) {
      captureException(e, {
        fingerprint: ['clipboard.destroy', 'new-settings', 'workspace.general'],
      });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Workspace::General': typeof General;
  }
}
